/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect } from 'react'
import { useServiceContext } from '../../root'
import { Workflow } from './types'
import { DetailedWorkflow } from '../../services/api/workflows'
import ActionsMenu from '../../components/shared/ActionsMenu'
import { useWorkflowsService } from '../../hooks/useWorkflowsService'
import { useModal } from '../../contexts/ModalContext'
import WorkflowDetailsModal from '../../components/modals/WorkflowDetailsModal'
import WorkflowCommentsModal from '../../components/modals/WorkflowCommentsModal'
import { WorkflowEditModal } from '../../components/modals/WorkflowEditModal'
import { WorkflowApproversModal } from '../../components/modals/WorkflowApproversModal'
import { WorkflowReviewModal } from '../../components/modals/WorkflowReviewModal'
import { WorkflowTraineesModal } from '../../components/modals/WorkflowTraineesModal'
import { WorkflowRejectionReviewersModal } from '../../components/modals/WorkflowRejectionReviewersModal'
import { WorkflowQcApproversModal } from '../../components/modals/WorkflowQcApproversModal'
import { WorkflowEffectiveDateModal } from '../../components/modals/WorkflowEffectiveDateModal'
import { WorkflowCollaboratorsModal } from '../../components/modals/WorkflowCollaboratorsModal'

interface WorkflowsTableMenuProps {
  workflow: Workflow
  multiSelectOptions?: {
    selectedIds?: number[]
    selectedWholeResult?: boolean
    selectedItems?: Workflow[]
    deselectAll?: () => void
  }
}

interface MenuItemData {
  action?: () => void
  label?: string
  disabled?: boolean
  value?: string
  title?: string
  submenu?: MenuItemData[]
  divider?: boolean
}

/**
 * Workflows Table Actions Menu Component
 *
 * Provides context menu with available actions for workflows:
 * - Show Detail
 * - View Comments
 * - Resume (if suspended)
 * - Cancel (if active)
 * - Update Effective Date
 * - Manage submenu (Collaborators, Edit, Reviewers, Approvers, Trainees, Rejection Reviewers)
 *
 * Supports multi-selection mode where only Cancel action is shown for selected workflows.
 * Based on lego/page/workflows/TableMenu.jsx
 */
const WorkflowsTableMenu: FC<WorkflowsTableMenuProps> = ({ workflow, multiSelectOptions }) => {
  const { api } = useServiceContext()
  const { showModal } = useModal()
  const { resume, cancel } = useWorkflowsService()
  const [isOpen, setIsOpen] = useState(false)
  const [wasJustOpened, setWasJustOpened] = useState(false)
  const [items, setItems] = useState<MenuItemData[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  // Multi-selection state
  const isWholeResultSelection = multiSelectOptions?.selectedWholeResult
  const isMultiSelection = multiSelectOptions?.selectedIds && multiSelectOptions.selectedIds.length > 0
  const isMultiButOnlyOneSelection =
    isMultiSelection &&
    multiSelectOptions?.selectedIds?.length === 1 &&
    multiSelectOptions.selectedIds[0] === workflow.id
  const isWorkflowIncludedInMultiSelection = isMultiSelection && multiSelectOptions?.selectedIds?.includes(workflow.id)

  /**
   * Build menu for multiple workflows selection
   * Only shows Cancel action or "No actions available" message
   */
  const buildMultipleWorkflowsMenu = (workflowData: DetailedWorkflow): void => {
    const menu: MenuItemData[] = []

    if (isWholeResultSelection || (isMultiSelection && !isMultiButOnlyOneSelection && workflowData?.flags.canCancel)) {
      menu.push({
        value: 'ACTION_COMMAND_CANCEL',
        action: () => {
          const idsToCancel =
            multiSelectOptions?.selectedItems && multiSelectOptions.selectedItems.length > 1
              ? multiSelectOptions.selectedIds || []
              : [workflowData.id]
          cancel(idsToCancel, workflow)
        }
      })
    } else if (
      isWholeResultSelection ||
      (isMultiSelection && !isMultiButOnlyOneSelection && !workflowData?.flags.canCancel)
    ) {
      menu.push({
        value: 'ACTION_MENU_NO_ACTIONS_AVAILABLE',
        action: () => {},
        disabled: true
      })
    }

    setItems(menu)
  }

  /**
   * Build full menu options for single workflow
   * Includes all available actions based on workflow flags
   */
  const buildSingleWorkflowMenu = (workflowData: DetailedWorkflow): void => {
    const menu: MenuItemData[] = []
    const subMenu: MenuItemData[] = []

    // Show Detail (always available)
    menu.push({
      value: 'SHOW_DETAIL',
      action: () => {
        showModal(<WorkflowDetailsModal wfId={workflowData.id} />)
      }
    })

    // View Comments
    if (workflowData?.flags.canViewComments) {
      menu.push({
        value: 'VIEW_COMMENTS_ACTION',
        action: () => {
          showModal(<WorkflowCommentsModal workflow={workflowData} />)
        }
      })
    }

    // Resume (if suspended)
    if (workflowData?.flags.canResume) {
      menu.push({
        value: 'ACTION_COMMAND_RESUME',
        action: () => {
          resume(workflowData.id)
        }
      })
    }

    // Cancel
    if (workflowData?.flags.canCancel) {
      menu.push({
        value: 'ACTION_COMMAND_CANCEL',
        action: () => {
          const idsToCancel =
            multiSelectOptions?.selectedItems && multiSelectOptions.selectedItems.length > 1
              ? multiSelectOptions.selectedIds || []
              : [workflowData.id]
          cancel(idsToCancel, workflow)
        }
      })
    }

    // Update Effective Date
    if (workflowData?.flags.canUpdateEffectiveDate) {
      menu.push({
        value: 'ACTION_COMMAND_UPDATE_EFFECTIVE_DATE',
        action: () => {
          showModal(<WorkflowEffectiveDateModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    // Build Manage submenu
    // Add Collaborators
    if (workflowData?.canAddUserToNOfAllTaskGroup) {
      subMenu.push({
        value: 'CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE',
        action: () => {
          showModal(<WorkflowCollaboratorsModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    // Edit Workflow
    if (workflowData?.canEditWorkflow) {
      subMenu.push({
        value: 'EDIT',
        action: () => {
          showModal(<WorkflowEditModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    // Reviewers
    if (workflowData?.canEditWorkflowReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowReviewModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    // Approvers
    if (workflowData?.canEditWorkflowApprovers) {
      const isQcReview = workflowData.workflowType === 'QC_REVIEW'
      subMenu.push({
        value: isQcReview ? 'WF_PHASE_QC_APPROVAL' : 'BROWSER_ACTION_APPROVERS',
        action: () => {
          if (isQcReview) {
            showModal(<WorkflowQcApproversModal workflowId={workflowData.id} workflow={workflowData} />)
          } else {
            showModal(<WorkflowApproversModal workflowId={workflowData.id} workflow={workflowData} />)
          }
        }
      })
    }

    // Trainees
    if (workflowData?.canEditWorkflowTrainees) {
      subMenu.push({
        value: 'BROWSER_ACTION_TRAINEES',
        action: () => {
          showModal(<WorkflowTraineesModal workflowId={workflowData.id} workflow={workflowData} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    // Rejection Reviewers
    if (workflowData?.canEditRejectionReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REJECTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowRejectionReviewersModal workflowId={workflowData.id} workflow={workflowData} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    // Add Manage submenu if there are any items
    if (subMenu.length > 0) {
      menu.push({ value: 'MANAGE', submenu: subMenu })
    }

    setItems(menu)
  }

  /**
   * Load workflow data and build menu when opened
   * Only run logic when menu is first opened (wasJustOpened), not on every dependency change
   */
  useEffect(() => {
    if (isOpen && wasJustOpened) {
      // Reset the flag after processing
      setWasJustOpened(false)

      if (isMultiSelection && !isWorkflowIncludedInMultiSelection) {
        api.workflows.getWorkflow(workflow.id).then((data) => {
          buildSingleWorkflowMenu(data)
        })
        // if the action menu was opened for file which is not in the current multiselection, deselect all
        multiSelectOptions?.deselectAll?.()
      } else if (
        isWholeResultSelection ||
        (isMultiSelection && !isMultiButOnlyOneSelection && isWorkflowIncludedInMultiSelection)
      ) {
        api.workflows.getWorkflow(workflow.id).then((data) => {
          buildMultipleWorkflowsMenu(data)
        })
        // if the action menu was opened for one of the multi-selected workflows, show only cancel action.
      } else {
        api.workflows.getWorkflow(workflow.id).then((data) => {
          buildSingleWorkflowMenu(data)
        })
      }
    }
  }, [
    workflow.id,
    isOpen,
    isMultiSelection,
    isMultiButOnlyOneSelection,
    isWorkflowIncludedInMultiSelection,
    isWholeResultSelection,
    wasJustOpened
  ])

  return (
    <ActionsMenu
      menuId={`action-menu-workflows-${workflow.id}`}
      onToggle={(open) => {
        if (open && !isOpen) {
          // Only set wasJustOpened when transitioning from closed to open
          setWasJustOpened(true)
        }
        setIsOpen(open)
      }}
      items={items}
    />
  )
}

export default WorkflowsTableMenu
